import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2, XCircle, Coins, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Row, Hash, SimBadge } from "@/components/common";

/** Checkout Cuenta Nu (SIMULADO) dentro de un Dialog. request = {kind, ref, service, account, amount} */
export default function CheckoutDialog({ open, onOpenChange, request, onDone }) {
  const { refresh } = useAuth();
  const [quote, setQuote] = useState(null);
  const [useTokens, setUseTokens] = useState(false);
  const [scenario, setScenario] = useState("success");
  const [stage, setStage] = useState("quote"); // quote | processing | done
  const [payment, setPayment] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!open || !request) return;
    setStage("quote"); setPayment(null); setQuote(null); setUseTokens(false); setScenario("success");
    api.post("/payments/quote", request).then(({ data }) => setQuote(data)).catch((e) => {
      toast.error(errMsg(e)); onOpenChange(false);
    });
    return () => clearInterval(timer.current);
  }, [open, request]); // eslint-disable-line

  const pay = async () => {
    try {
      setStage("processing");
      const { data } = await api.post("/payments/checkout", { ...request, use_tokens: useTokens, scenario });
      setPayment(data);
      timer.current = setInterval(async () => {
        const { data: p } = await api.get(`/payments/${data.payment_id}`);
        setPayment(p);
        if (p.status !== "pendiente") {
          clearInterval(timer.current);
          setStage("done");
          refresh();
          onDone?.(p);
        }
      }, 1000);
    } catch (e) {
      setStage("quote");
      toast.error(errMsg(e));
    }
  };

  const discount = useTokens && quote ? quote.token_discount : 0;
  const total = quote ? quote.gross - discount : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (stage !== "processing") onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-[#820AD1] text-white text-xs font-bold">nu</span>
            Pagar con Cuenta Nu <SimBadge />
          </DialogTitle>
          <DialogDescription>Liquidación instantánea con confirmación por webhook.</DialogDescription>
        </DialogHeader>

        {!quote && <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}

        {quote && stage === "quote" && (
          <div className="space-y-4">
            <div className="rounded-lg bg-secondary p-4 space-y-2 text-sm">
              <div className="font-medium">{quote.concept}</div>
              <Row label="Importe"><span className="font-mono">{money(quote.gross)}</span></Row>
              {quote.early_discount && <Row label="Original"><span className="font-mono line-through text-muted-foreground">{money(quote.original)}</span></Row>}
              {useTokens && <Row label="Descuento tokens HGT"><span className="font-mono text-emerald-700">−{money(discount)}</span></Row>}
              <Row label="Total"><span className="font-mono text-lg font-semibold" data-testid="checkout-total">{money(total)}</span></Row>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="flex items-center gap-2">
                <Coins className="h-4 w-4 text-amber-600" />
                <div>
                  <Label htmlFor="tok" className="text-sm">Aplicar tokens HGT</Label>
                  <div className="text-xs text-muted-foreground">{quote.max_tokens > 0 ? `Usar ${quote.max_tokens} HGT (máx. 20%)` : "Sin tokens disponibles"}</div>
                </div>
              </div>
              <Switch id="tok" checked={useTokens} disabled={!quote.max_tokens} onCheckedChange={setUseTokens} data-testid="checkout-use-tokens-switch" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Escenario de prueba (simulador)</Label>
              <Select value={scenario} onValueChange={setScenario}>
                <SelectTrigger data-testid="checkout-scenario-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="success">Pago aprobado</SelectItem>
                  <SelectItem value="failure">Pago rechazado (fondos insuficientes)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full btn-press" onClick={pay} data-testid="checkout-pay-button">
              <Zap className="h-4 w-4 mr-1" /> Pagar {money(total)}
            </Button>
          </div>
        )}

        {stage === "processing" && (
          <div className="py-8 flex flex-col items-center gap-3 text-center" data-testid="checkout-processing">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <div className="font-medium">Esperando confirmación de Cuenta Nu…</div>
            <div className="text-xs text-muted-foreground font-mono">{payment?.charge_id}</div>
          </div>
        )}

        {stage === "done" && payment && (
          <div className="space-y-4" data-testid="checkout-result">
            {payment.status === "pagado" ? (
              <div className="flex flex-col items-center text-center gap-2">
                <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                <div className="font-semibold text-lg" data-testid="checkout-success">Pago confirmado</div>
              </div>
            ) : (
              <div className="flex flex-col items-center text-center gap-2">
                <XCircle className="h-10 w-10 text-rose-600" />
                <div className="font-semibold text-lg" data-testid="checkout-failed">Pago rechazado</div>
                <div className="text-sm text-muted-foreground">{payment.failure_reason}</div>
              </div>
            )}
            <div className="rounded-lg border p-3 space-y-2 text-sm">
              <Row label="Folio de pago"><span className="font-mono text-xs">{payment.folio}</span></Row>
              <Row label="Monto"><span className="font-mono">{money(payment.amount)}</span></Row>
              {payment.receipt_hash && <Row label="Hash de recibo"><Hash value={payment.receipt_hash} /></Row>}
              {payment.anchor && <Row label="Tx Polygon"><Hash value={payment.anchor.tx_hash} n={8} /></Row>}
            </div>
            <Button variant="secondary" className="w-full" onClick={() => onOpenChange(false)} data-testid="checkout-close-button">Cerrar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
